/**
 * Catálogo do domínio `contratos` (T52, CAT-01).
 *
 * Origem: `src/shared/autorizacao/contrato.ts`.
 *
 * Mesmo formato de acoplamento oculto de `materiais`, e por isso mesmo
 * tratamento: as três concessões por relação (`responsavel`, `gestor`,
 * `validador`) vivem dentro de `if (user.temPermissao(["contratos"]))`
 * (`contrato.ts:11`), então moram em `contratos.leitura`, que é o papel para onde
 * `LEGADO.contratos` aponta. Quem tem o papel tem o que tem hoje.
 *
 * As três são o exemplo mais completo do catálogo: relação **e** estado na mesma
 * concessão, com um conjunto de estados diferente por relação — é o que o `§D3`
 * do `ADR-0004` descreve e o que as áreas confirmaram sem alteração (AD-030).
 */
import type { Concessao, NomePapel, Papel } from "../catalogo/vocabulario";

/** Nada é concedido fora do gate de módulo. */
export const CONCESSOES_BASE_CONTRATOS: readonly Concessao[] = [];

export const PAPEIS_CONTRATOS = {
  "contratos.admin": {
    dominio: "contratos",
    nivel: "admin",
    concessoes: [{ acoes: ["manage"], recurso: "Contratos" }],
  },
  "contratos.escrita": {
    dominio: "contratos",
    nivel: "escrita",
    concessoes: [
      { acoes: ["create", "read"], recurso: "Contratos" },
      { acoes: ["edit"], recurso: "Contratos", estados: ["Revisão"] },
    ],
  },
  /** O papel do gate de módulo: leitura mais as três relações de responsabilidade. */
  "contratos.leitura": {
    dominio: "contratos",
    nivel: "leitura",
    concessoes: [
      { acoes: ["read"], recurso: "Contratos" },
      {
        acoes: ["edit"],
        recurso: "Contratos",
        exigeRelacao: ["responsavel"],
        estados: ["Revisão"],
      },
      {
        acoes: ["edit"],
        recurso: "Contratos",
        exigeRelacao: ["gestor"],
        estados: ["Emitido para o Jurídico", "Revisão para cancelamento"],
      },
      {
        acoes: ["edit"],
        recurso: "Contratos",
        exigeRelacao: ["validador"],
        estados: ["Emitido para o verificador", "Em finalização"],
      },
    ],
  },
  /**
   * A concessão de site manager sobre `Contratos`, agora com dono
   * (`contrato.ts:47-53`, AD-031 D5).
   *
   * Fica fora de `contratos.leitura` porque tem **outro conjunto de estados** para
   * a mesma relação: o gestor do contrato edita em "Emitido para o Jurídico" e
   * "Revisão para cancelamento"; o site manager que é gestor edita em "Aprovado".
   * Somar ao papel de leitura daria o estado "Aprovado" a todo gestor.
   *
   * O usuário confirmou que o comportamento está certo — *"é isso mesmo, qual o
   * problema?"*. O problema nunca foi o comportamento, era onde declarar; e o
   * `ADR-0004 §D3` já tinha a resposta, o nível `escrita_propria`.
   */
  "contratos.escrita_propria": {
    dominio: "contratos",
    nivel: "escrita_propria",
    concessoes: [
      { acoes: ["read"], recurso: "Contratos" },
      {
        acoes: ["edit"],
        recurso: "Contratos",
        exigeRelacao: ["gestor"],
        estados: ["Aprovado"],
      },
    ],
  },
} as const satisfies Partial<Record<NomePapel, Papel>>;

/**
 * @deprecated Virou o papel `contratos.escrita_propria` na T56 (AD-031 D5).
 *
 * Continua exportada e apontando para o papel para não quebrar quem a importe.
 */
export const CONCESSOES_SITE_MANAGER_CONTRATOS: readonly Concessao[] = [
  ...PAPEIS_CONTRATOS["contratos.escrita_propria"].concessoes,
];
